/**
 * The queue as the screen sees it: the entries waiting for the current user, a short notice
 * ("Saved on the device"), and the sending itself (when the app opens, when the connection
 * returns, when the person comes back to the app, on a timer while there is something to send,
 * and on the button). A network connection alone does not prove the server answers, so a
 * failed send just leaves the entries where they are.
 */

import { useEffect, useSyncExternalStore } from "react";
import { available } from "./db";
import { listQueue, syncQueue, type QueueItem, type SyncSummary } from "./queue";
import { onQueued } from "./events";
import { useAuthStore } from "../stores/auth-store";

interface State {
  items: QueueItem[];
  /** entries of other users on this device: they are not sent for the current user */
  others: number;
  notice: string | null;
  syncing: boolean;
  last?: SyncSummary;
}

let state: State = { items: [], others: 0, notice: null, syncing: false };
const listeners = new Set<() => void>();
const set = (next: Partial<State>) => {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const getQueueState = (): State => state;
export const useQueueState = (): State => useSyncExternalStore(subscribe, getQueueState, getQueueState);

/** Load the queue for the current user. */
export async function refreshQueue(): Promise<void> {
  const userId = useAuthStore.getState().user?.id;
  if (!available() || !userId) return set({ items: [], others: 0 });
  const everything = await listQueue();
  set({ items: everything.filter((i) => i.userId === userId), others: everything.filter((i) => i.userId !== userId).length });
}

let noticeTimer: ReturnType<typeof setTimeout> | undefined;
export function showNotice(text: string, ms = 6000): void {
  set({ notice: text });
  if (noticeTimer) clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => set({ notice: null }), ms);
}

/** The function that sends one entry (set by the API module, so this file does not depend on it). */
let sender: ((item: QueueItem) => Promise<unknown>) | null = null;
export const setSender = (fn: (item: QueueItem) => Promise<unknown>) => {
  sender = fn;
};

let running: Promise<SyncSummary | undefined> | null = null;

/** Send what can be sent now. Only one send runs at a time (also across tabs where the browser has locks). */
export function runSync(): Promise<SyncSummary | undefined> {
  const userId = useAuthStore.getState().user?.id;
  if (!userId || !sender || !available()) return Promise.resolve(undefined);
  if (running) return running;
  const send = sender;
  const work = async () => {
    set({ syncing: true });
    try {
      const summary = await syncQueue(userId, send);
      set({ last: summary });
      return summary;
    } finally {
      await refreshQueue();
      set({ syncing: false });
    }
  };
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  const started: Promise<SyncSummary | undefined> = locks
    ? (locks.request("seaslot-sync", { ifAvailable: true }, async (lock) => (lock ? await work() : undefined)) as unknown as Promise<SyncSummary | undefined>)
    : work();
  const result = started.finally(() => {
    running = null;
  });
  running = result;
  return result;
}

onQueued(() => {
  showNotice("saved");
  void refreshQueue();
});

/** Send when the app opens, when the connection returns, when the person comes back, and now and then while there is something to send. */
export function useAutoSync(): void {
  const userId = useAuthStore((s) => s.user?.id);
  useEffect(() => {
    if (!userId) return;
    void refreshQueue().then(() => runSync());
    const again = () => void runSync();
    window.addEventListener("online", again);
    const visible = () => document.visibilityState === "visible" && again();
    document.addEventListener("visibilitychange", visible);
    const timer = setInterval(() => {
      if (getQueueState().items.length > 0) again();
    }, 60_000);
    return () => {
      window.removeEventListener("online", again);
      document.removeEventListener("visibilitychange", visible);
      clearInterval(timer);
    };
  }, [userId]);
}

/** For tests. */
export function resetQueueStateForTests(): void {
  state = { items: [], others: 0, notice: null, syncing: false };
  running = null;
  listeners.forEach((l) => l());
}
